import React from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  Banknote,
  CheckCircle2,
  Info,
  ShieldCheck,
} from "lucide-react";
import SalesAffiliateLayout from "../components/SalesAffiliateLayout";
import { useSalesAffiliateWorkspace } from "../context/SalesAffiliateWorkspaceContext";
import { salesAffiliatePaymentSetupGuide as guide } from "../content/salesAffiliatePaymentSetupGuide";

const helperToneClasses = {
  blue: "border-blue-200 bg-blue-50 text-blue-900",
  amber: "border-amber-200 bg-amber-50 text-amber-900",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  slate: "border-slate-200 bg-slate-50 text-slate-800",
};

function GuideHelper({ helper }) {
  const toneClass = helperToneClasses[helper.tone] || helperToneClasses.slate;

  return (
    <div className={`mt-4 rounded-xl border px-4 py-3 text-sm leading-6 ${toneClass}`}>
      <span className="font-black">{helper.label}:</span>{" "}
      <span className="font-semibold">{helper.text}</span>
    </div>
  );
}

function GuideSection({ section }) {
  return (
    <section
      id={section.id}
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
    >
      <h2 className="text-lg font-black text-slate-950">{section.title}</h2>

      {section.paragraphs?.map((paragraph) => (
        <p key={paragraph} className="mt-3 text-sm leading-6 text-slate-700">
          {paragraph}
        </p>
      ))}

      {section.bullets?.length ? (
        <ul className="mt-4 space-y-2 pl-5 text-sm leading-6 text-slate-700">
          {section.bullets.map((item) => (
            <li key={item} className="list-disc pl-1">
              {item}
            </li>
          ))}
        </ul>
      ) : null}

      {section.helpers?.map((helper) => (
        <GuideHelper key={`${section.id}-${helper.label}-${helper.text}`} helper={helper} />
      ))}

      {section.subheading ? (
        <h3 className="mt-5 text-sm font-black text-slate-950">{section.subheading}</h3>
      ) : null}

      {section.footer ? (
        <p className="mt-3 text-sm font-semibold leading-6 text-slate-700">{section.footer}</p>
      ) : null}

      {section.trailingSubheading ? (
        <h3 className="mt-5 text-sm font-black text-slate-950">
          {section.trailingSubheading}
        </h3>
      ) : null}

      {section.trailingParagraphs?.map((paragraph) => (
        <p key={paragraph} className="mt-3 text-sm leading-6 text-slate-700">
          {paragraph}
        </p>
      ))}

      {section.trailingHelpers?.map((helper) => (
        <GuideHelper
          key={`${section.id}-trailing-${helper.label}-${helper.text}`}
          helper={helper}
        />
      ))}
    </section>
  );
}

export default function SalesAffiliatePaymentSetupGuide() {
  const {
    loading,
    statusLoadFailed,
    errorMessage,
    workspaceData,
    configuration,
  } = useSalesAffiliateWorkspace();

  if (loading) {
    return (
      <SalesAffiliateLayout activeSection="onboarding-payment">
        <div className="flex min-h-[420px] items-center justify-center px-6">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 shadow-sm">
            Loading Payment Setup Guide…
          </div>
        </div>
      </SalesAffiliateLayout>
    );
  }

  if (statusLoadFailed) {
    return (
      <SalesAffiliateLayout activeSection="onboarding-payment">
        <div className="mx-auto flex min-h-[420px] max-w-2xl items-center px-6">
          <div className="w-full rounded-2xl border border-amber-200 bg-amber-50 px-5 py-5 text-sm font-semibold leading-6 text-amber-900">
            <div className="font-black">Sales Affiliate workspace unavailable</div>
            <p className="mt-1">
              {errorMessage || "We could not verify your Sales Affiliate workspace. Return to Payment Setup and try again."}
            </p>
            <Link
              to="/sales-affiliate/onboarding#payment"
              className="mt-4 inline-flex rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-black text-white hover:bg-slate-800"
            >
              Return to Payment Setup
            </Link>
          </div>
        </div>
      </SalesAffiliateLayout>
    );
  }

  if (!workspaceData?.affiliate_exists || !configuration?.allowsOnboardingPage) {
    return <Navigate to={configuration?.defaultPath || "/sales-affiliate/application"} replace />;
  }

  return (
    <SalesAffiliateLayout activeSection="onboarding-payment">
      <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <Link
          to="/sales-affiliate/onboarding#payment"
          className="inline-flex items-center gap-2 text-sm font-black text-blue-700 hover:text-blue-900"
        >
          <ArrowLeft size={16} />
          Back to Payment Setup
        </Link>

        <div className="mt-5 rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-6 shadow-sm sm:p-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-100 text-blue-800">
              <Banknote size={24} />
            </div>
            <div>
              <div className="text-xs font-black uppercase tracking-[0.2em] text-blue-700">
                Sales Affiliate Guide
              </div>
              <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
                {guide.title}
              </h1>
              {guide.subtitle ? (
                <p className="mt-2 text-base font-black leading-7 text-slate-800">
                  {guide.subtitle}
                </p>
              ) : null}
              <p className="mt-4 max-w-3xl text-base font-semibold leading-7 text-slate-700">
                {guide.activationClarification}
              </p>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                {guide.introduction}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <CheckCircle2 className="text-emerald-700" size={22} />
            <div className="mt-2 text-sm font-black text-emerald-950">
              Activation remains separate
            </div>
            <p className="mt-1 text-xs font-semibold leading-5 text-emerald-800">
              You can activate and sign up Prospects and Customers before Payment Setup is complete.
            </p>
          </div>

          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
            <ShieldCheck className="text-blue-700" size={22} />
            <div className="mt-2 text-sm font-black text-blue-950">
              Sensitive information stays with the provider
            </div>
            <p className="mt-1 text-xs font-semibold leading-5 text-blue-800">
              The Payment Account Provider handles banking and identity-verification information.
            </p>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <Info className="text-amber-700" size={22} />
            <div className="mt-2 text-sm font-black text-amber-950">
              Link is optional
            </div>
            <p className="mt-1 text-xs font-semibold leading-5 text-amber-800">
              Save account with Link is optional; Finish without saving is acceptable for AFP.
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {guide.sections.map((section) => (
            <GuideSection key={section.id} section={section} />
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm leading-6 text-slate-700">
          <div className="font-black text-slate-950">Current provider note</div>
          <p className="mt-1">
            AFP currently uses Stripe as its Payment Account Provider. Provider-hosted screens and labels can change over time. Follow the instructions shown by the Payment Account Provider and return to AFP when finished.
          </p>
        </div>

        <div className="mt-6">
          <Link
            to="/sales-affiliate/onboarding#payment"
            className="inline-flex items-center justify-center rounded-xl bg-blue-700 px-5 py-3 text-sm font-black text-white hover:bg-blue-800"
          >
            Return to Commission Payment Setup
          </Link>
        </div>
      </div>
    </SalesAffiliateLayout>
  );
}
